import React, { useCallback, useRef, useState } from 'react';
import UserInfoComponent from './UserInfoComponent';
import UserGroupsComponent from './UserGroupsComponent';
import { useAuthUserData, useServerErrorHandling, useToast } from 'hooks';
import { useChangeTrackingLens, get } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { FakeText } from '@totalsoft/rocket-ui';
import { useMutation, useSubscription } from '@apollo/client';
import { UPDATE_USER_ADDITIONAL_DATA_MUTATION } from './mutations';
import { USER_ADDITIONAL_DATA_UPDATED } from './subscriptions';

const initialModel = {
  avatar: null
};

const ProfileContainer = () => {
  const { authUser, loading } = useAuthUserData();
  const [profileData, dirtyInfo, resetProfileData] = useChangeTrackingLens(initialModel);
  const editor = useRef(null);
  const addToast = useToast();

  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  const [updateUserAdditionalData] = useMutation(UPDATE_USER_ADDITIONAL_DATA_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.updateUserAdditionalData;
      storeKey({ correlationId });
    }
  });

  useSubscription(USER_ADDITIONAL_DATA_UPDATED, {
    onData: ({ data: { data } }) => {
      setSaving(false);
      const pd = profileData |> get;
      resetProfileData({ ...pd, ...data.userAdditionalDataUpdated });
    }
  });

  const handleUpdateUserAdditionalData = useCallback(
    async editor => {
      setSaving(true);

      if (!editor.current) {
        await updateUserAdditionalData({ variables: { input: { avatar: null } } });
        return;
      }
      const canvas = editor.current.getImage();
      const resizedPhoto = handleResize(canvas);

      await updateUserAdditionalData({
        variables: { input: { avatar: resizedPhoto } }
      });
    },
    [updateUserAdditionalData]
  );

  const handleResize = canvas => {
    const offScreenCanvas = document.createElement('canvas');
    const width = 300;
    const height = 300;
    offScreenCanvas.width = width;
    offScreenCanvas.height = height;
    offScreenCanvas.getContext('2d', { willReadFrequently: true }).drawImage(canvas, 0, 0, width, height);
    return offScreenCanvas.toDataURL('image/png', 0.9);
  };

  return (
    <>
      {!authUser || loading ? (
        <FakeText />
      ) : (
        <>
          <UserInfoComponent
            profileDataLens={profileData}
            personData={authUser.person}
            toggleExpanded={toggleExpanded}
            expanded={expanded}
            onSaveUserInfo={handleUpdateUserAdditionalData}
            editor={editor}
            saving={saving}
            isDirtyInfo={isDirty(dirtyInfo)}
          />
          <UserGroupsComponent authUserData={authUser} />
        </>
      )}
    </>
  );
};

export default ProfileContainer;
